/**
 * The id a per-user-per-DAY ad conversion is capped and deduped on.
 *
 * Shared by every platform that asked for "one conversion per browser per day"
 * rather than one per search: Meta (DUP-2188, both transports —
 * `metaConversions.ts` and `metaAdsConversion.ts`) and TikTok (DUP-2261,
 * `tiktokConversions.ts`). Google Ads is deliberately NOT a consumer — see
 * `googleAdsConversions.ts`, which stamps a per-SEARCH id on purpose.
 *
 * Wallace's requirement (2026-09) — a person who runs 2-3 dupes should be ONE
 * conversion, not three. These are ad clicks, so only the first conversion of
 * the day carries information; the rest inflate the platform's picture of who
 * converts and bias optimisation toward heavy searchers. Measured on Meta
 * before the cap: 17% of conversions sent were repeat searches by someone
 * already counted.
 *
 * ⚠️ THE CAP IS PER BROWSER, NOT PER PERSON. `tempUserId` is a uuid minted
 * per browser profile (`useGetTempUserId`), so the same human on a phone and
 * a laptop has two ids, produces two conversion ids, and gets two conversions
 * — the platform cannot collapse them, because its dedup matches on the id we
 * send. That is an accepted limit, not an oversight: ad clicks land on one
 * device, which is the journey this cap exists for. Do not describe it as
 * per-person.
 *
 * ⛔ Do NOT "improve" this by preferring an authenticated user id. It would be
 * per-person only for the signed-in half, and it would BREAK the cap across a
 * login: someone anonymous in the morning and signed in that afternoon would
 * key on two different ids and get two conversions. `tempUserId` survives that
 * boundary, which is why it is the identity here.
 *
 * ⚠️ The date is the USER'S LOCAL date, deliberately. "One per day" means
 * their day; a UTC boundary would split a single evening's session in two for
 * anyone west of Greenwich. Built from `getFullYear`/`getMonth`/`getDate`
 * rather than `toLocaleDateString`, whose output varies with the host's ICU
 * data and would silently key two machines differently.
 *
 * ⚠️ Every consumer stores its "already fired" mark in `localStorage` keyed
 * with this id, and prunes marks whose date is not today. The `:YYYY-MM-DD`
 * suffix is therefore load-bearing: those sweeps recognise a dated key by it.
 * Changing the shape here means changing every consumer's prune.
 */
export const dailyConversionId = (userId: string): string =>
  `${userId}:${todayStamp()}`

/** `YYYY-MM-DD` in the user's LOCAL timezone. See `dailyConversionId`. */
export const todayStamp = (): string => {
  const now = new Date()
  const month = `${now.getMonth() + 1}`.padStart(2, '0')
  const day = `${now.getDate()}`.padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const DATED_MARK = /:\d{4}-\d{2}-\d{2}$/
const UNDATED_MARK_TTL_MS = 24 * 60 * 60 * 1000

/**
 * The sweep every consumer owes its marks, for the ones that share it. Keys
 * under `prefix` are judged in three classes:
 *
 *   kept (`keepPrefix`) — retained until browser storage is cleared, e.g. an
 *   account's signup mark, which does not expire with a daily cap.
 *
 *   dated (`…:<YYYY-MM-DD>`) — anything not from today is dead, since the id
 *   it would match can never be generated again.
 *
 *   undated (a per-search fallback key) — NOT safe to drop on sight: removing
 *   one mid-session lets that same search re-fire. Aged out on the stored
 *   timestamp (the mark's value) after 24h instead.
 *
 * Best-effort and silent: a storage failure must never block a conversion.
 */
export const pruneDailyMarks = (prefix: string, keepPrefix?: string): void => {
  try {
    const today = todayStamp()
    const cutoff = Date.now() - UNDATED_MARK_TTL_MS
    const stale: string[] = []

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i)
      if (!k?.startsWith(prefix)) continue
      if (keepPrefix && k.startsWith(keepPrefix)) continue

      if (DATED_MARK.test(k)) {
        if (!k.endsWith(`:${today}`)) stale.push(k)
        continue
      }

      const writtenAt = Number.parseInt(
        window.localStorage.getItem(k) ?? '',
        10
      )
      if (!Number.isFinite(writtenAt) || writtenAt < cutoff) stale.push(k)
    }

    for (const k of stale) window.localStorage.removeItem(k)
  } catch {
    // localStorage can be unavailable in privacy-restricted browsers.
  }
}
